export function usesCountryHomeFeed(state) {
  return (
    (state?.primaryCategory ?? 'all') === 'all' &&
    (state?.sortId ?? 'trending') === 'trending' &&
    !String(state?.appliedQuestion ?? '').trim()
  );
}

export function hasPendingEmptyCountryPage(state) {
  return Boolean(
    usesCountryHomeFeed(state) &&
      state?.hasMore &&
      state?.rawRows?.length === 0 &&
      state?.homeFeedCursor?.feedToken &&
      Number.isInteger(state.homeFeedCursor.nextOffset) &&
      state.homeFeedCursor.nextOffset >= 0
  );
}

const MAX_SPARSE_PAGE_REQUESTS = 3;

function uniqueRows(rows) {
  const seen = new Set();
  const positions = new Set();
  return rows.filter((row) => {
    const key = String(row.id);
    if (seen.has(key) || positions.has(row.homeFeedPosition)) return false;
    seen.add(key);
    positions.add(row.homeFeedPosition);
    return true;
  });
}

export async function fetchCountryHomePage(ctx, fetchPage) {
  const { state, append, patchExisting, pageSize } = ctx;
  const cursor = state.homeFeedCursor;
  const generation = state.loadGeneration;
  const continuing = Boolean((append || patchExisting) && cursor?.feedToken);
  const firstPageEnd = continuing
    ? cursor.firstPageEnd ?? Math.min(pageSize, cursor.nextOffset)
    : 0;
  if (
    continuing &&
    (!Number.isInteger(firstPageEnd) || firstPageEnd < 0 || firstPageEnd > cursor.nextOffset)
  ) {
    throw new Error('Invalid home feed cursor response');
  }
  let refreshing = Boolean(patchExisting && continuing);
  let offset = continuing && append ? cursor.nextOffset : 0;
  let token = continuing ? cursor.feedToken : '';
  const continuingAppend = Boolean(append && continuing);
  let restartUsed = false;
  let merged = continuingAppend ? state.rawRows : [];
  let nextPage = continuingAppend ? ctx.pageNum + 1 : 1;
  let preservePagination = refreshing;
  let feedCursor = cursor;
  let hasMore = state.hasMore;
  let sparseRequests = 0;

  while (refreshing || sparseRequests < MAX_SPARSE_PAGE_REQUESTS) {
    if (generation !== state.loadGeneration) throw new Error('Stale home feed request');
    const requestPageSize = refreshing ? Math.min(pageSize, firstPageEnd - offset) : pageSize;
    if (!refreshing) sparseRequests += 1;
    let data = await fetchPage({
      feedToken: token,
      offset,
      pageSize: requestPageSize,
      refresh: refreshing,
    });
    if (generation !== state.loadGeneration) throw new Error('Stale home feed request');
    if (data?.restart) {
      if (restartUsed) throw new Error('Invalid home feed cursor response');
      restartUsed = true;
      refreshing = false;
      preservePagination = false;
      offset = 0;
      token = '';
      merged = [];
      nextPage = 1;
      data = await fetchPage({ feedToken: '', offset: 0, pageSize });
      sparseRequests = 1;
      if (generation !== state.loadGeneration) throw new Error('Stale home feed request');
    }
    if (
      data?.restart ||
      !data?.feedToken ||
      (token && data.feedToken !== token) ||
      !Array.isArray(data.rows) ||
      !Array.isArray(data.positions) ||
      data.positions.length !== data.rows.length ||
      typeof data.hasMore !== 'boolean' ||
      !Number.isInteger(data.nextOffset) ||
      data.nextOffset < offset ||
      (data.hasMore && data.nextOffset === offset) ||
      (refreshing &&
        (data.nextOffset !== offset + requestPageSize ||
          (data.nextOffset < firstPageEnd && !data.hasMore))) ||
      data.positions.some(
        (position) =>
          !Number.isInteger(position) || position < offset || position >= data.nextOffset
      )
    ) {
      throw new Error('Invalid home feed cursor response');
    }
    const rows = data.rows.map((row, index) => ({
      ...row,
      homeFeedPosition: data.positions[index],
    }));
    if (refreshing) {
      merged = merged.concat(rows);
      offset = data.nextOffset;
      // Sparse first pages can consume more slots than the number of visible cards.
      if (offset < firstPageEnd) continue;
      merged = uniqueRows(
        merged.concat(state.rawRows.filter((row) => row.homeFeedPosition >= firstPageEnd))
      );
      // Only an entirely empty refresh needs to continue past the consumed prefix.
      if (merged.length || !state.hasMore) break;
      refreshing = false;
      preservePagination = false;
      offset = cursor.nextOffset;
      token = cursor.feedToken;
      nextPage = ctx.pageNum + 1;
      continue;
    }
    merged = uniqueRows(merged.concat(rows));
    hasMore = data.hasMore;
    feedCursor = {
      feedToken: data.feedToken,
      nextOffset: data.nextOffset,
      // Appending must not expand the range refreshed on every minute-round change.
      firstPageEnd: continuing && !restartUsed ? firstPageEnd : data.nextOffset,
    };
    if (rows.length || !hasMore) break;
    token = feedCursor.feedToken;
    offset = feedCursor.nextOffset;
  }
  return {
    apiRows: merged,
    authoritativeRows: true,
    preservePagination,
    nextPage,
    hasMore,
    feedCursor,
  };
}
